import { useState } from "react";
import { Button, Input, Message } from "@arco-design/web-react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, post, setCsrf, showError } from "./api";
import { Logo } from "./components/Logo";
import { ThemeToggle } from "./theme";

export function Auth({ onLogin }: { onLogin: () => void }) {
  const provider = useQuery({ queryKey: ["auth-provider"], queryFn: () => api<{provider: string; signupUrl?: string | null}>("/auth/provider"), retry: 1 });
  const resetToken = new URLSearchParams(location.search).get("reset");
  const [mode, setMode] = useState<"login" | "register" | "recover">(
    resetToken ? "recover" : "login",
  );
  const [username, setUsername] = useState(""),
    [password, setPassword] = useState(""),
    [email, setEmail] = useState(""),
    [inviteCode, setInviteCode] = useState(""),
    [code, setCode] = useState("");
  const [busy, setBusy] = useState(false),
    [codes, setCodes] = useState<string[]>([]),
    [saved, setSaved] = useState(false);
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      if (mode === "recover") {
        await post("/auth/recover", {
          username,
          password,
          code,
          ...(resetToken ? { token: resetToken } : {}),
        });
        Message.success("密码已重置，请登录");
        setMode("login");
        history.replaceState(null, "", "/");
        return;
      }
      const data = await post(`/auth/${mode}`, {
        username,
        password,
        ...(mode === "register" ? { inviteCode: inviteCode.trim() } : {}),
        ...(mode === "register" && email ? { email } : {}),
      });
      setCsrf(data.csrf);
      if (data.recoveryCodes) setCodes(data.recoveryCodes);
      else onLogin();
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="auth-screen">
      <header className="auth-header">
        <a className="brand auth-brand" href="/">
          <Logo /> SUPER TODO
        </a>
        <ThemeToggle />
      </header>
      <section className="auth-form-wrap">
        <div className="auth-form">
          {provider.isPending ? <p className="muted">正在连接认证服务…</p> : provider.isError ? <><p>认证配置加载失败</p><Button onClick={() => provider.refetch()}>重试</Button></> : provider.data.provider === "casdoor" ? (
            <>
              <ShieldCheck size={32} className="accent" />
              <h2>欢迎回来</h2>
              <p className="muted">使用统一账号登录你的工作台，原有账号和密码继续有效。</p>
              {new URLSearchParams(location.search).has("auth_error") && <p role="alert">登录未完成或已过期，请重新登录。</p>}
              <Button className="primary-action" long size="large" onClick={() => location.assign(`/api/v1/auth/sso?returnTo=${encodeURIComponent(location.pathname + location.search)}`)}>统一账号登录 <ArrowRight size={16} /></Button>
              {provider.data.signupUrl ? <Button type="text" long href={provider.data.signupUrl}>还没有账号？注册统一账号</Button> : null}
            </>
          ) : codes.length ? (
            <>
              <ShieldCheck size={32} className="accent" />
              <h2>保存你的恢复码</h2>
              <p className="muted">
                每个恢复码只能使用一次。没有邮箱的账号，需要它来找回访问权限。
              </p>
              <div className="recovery-codes">
                {codes.map((c) => (
                  <code key={c}>{c}</code>
                ))}
              </div>
              <Button
                long
                onClick={() => {
                  const blob = new Blob([codes.join("\n")], {
                    type: "text/plain",
                  });
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(blob);
                  a.download = "SUPER-TODO-recovery-codes.txt";
                  a.click();
                  URL.revokeObjectURL(a.href);
                  setSaved(true);
                }}
              >
                下载恢复码
              </Button>
              <label className="check-label">
                <input
                  type="checkbox"
                  checked={saved}
                  onChange={(e) => setSaved(e.target.checked)}
                />
                我已安全保存这些恢复码
              </label>
              <Button
                className="primary-action"
                long
                disabled={!saved}
                onClick={onLogin}
              >
                进入我的工作台 <ArrowRight size={16} />
              </Button>
            </>
          ) : (
            <>
              <h2>
                {mode === "login"
                  ? "欢迎回来"
                  : mode === "register"
                    ? "建立你的私人空间"
                    : "找回账号"}
              </h2>
              <p className="muted">
                {mode === "login"
                  ? "登录你的工作台。"
                  : mode === "register"
                    ? "输入邀请码，创建账号后开始记录。"
                    : "使用一次性恢复码或邮件中的重置链接。"}
              </p>
              <form onSubmit={submit}>
                <label>
                  用户名{mode !== "register" && "或邮箱"}
                  <Input
                    autoComplete="username"
                    value={username}
                    onChange={setUsername}
                    placeholder="你的用户名"
                    required={!resetToken}
                  />
                </label>
                {mode === "register" && (
                  <label>
                    邀请码
                    <Input
                      autoComplete="off"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={inviteCode}
                      onChange={setInviteCode}
                      placeholder="输入邀请码"
                      maxLength={200}
                      required
                    />
                  </label>
                )}
                {mode === "register" && (
                  <label>
                    邮箱 <span className="muted">可选</span>
                    <Input
                      type="email"
                      value={email}
                      onChange={setEmail}
                      placeholder="用于验证与账号恢复"
                    />
                  </label>
                )}
                {mode === "recover" && !resetToken && (
                  <label>
                    恢复码
                    <Input
                      value={code}
                      onChange={setCode}
                      placeholder="xxxx-xxxx-xxxx-xxxx"
                    />
                  </label>
                )}
                <label>
                  {mode === "recover" ? "新密码" : "密码"}
                  <Input.Password
                    autoComplete={
                      mode === "login" ? "current-password" : "new-password"
                    }
                    value={password}
                    onChange={setPassword}
                    placeholder={
                      mode === "login" ? "输入密码" : "至少 10 个字符"
                    }
                    required
                  />
                </label>
                <Button
                  htmlType="submit"
                  className="primary-action"
                  long
                  size="large"
                  loading={busy}
                >
                  {mode === "login"
                    ? "进入工作台"
                    : mode === "register"
                      ? "创建账号"
                      : "重置密码"}{" "}
                  <ArrowRight size={16} />
                </Button>
              </form>
              <div className="auth-links">
                <button
                  onClick={() =>
                    setMode(mode === "register" ? "login" : "register")
                  }
                >
                  {mode === "register"
                    ? "已有账号？登录"
                    : "还没有账号？创建一个"}
                </button>
                <button
                  onClick={() =>
                    setMode(mode === "recover" ? "login" : "recover")
                  }
                >
                  {mode === "recover" ? "返回登录" : "忘记密码"}
                </button>
              </div>
              {mode === "recover" && (
                <Button
                  type="text"
                  onClick={() =>
                    post("/auth/request-email-reset", { username })
                      .then((r) => Message.info(r.message))
                      .catch(showError)
                  }
                >
                  向已验证邮箱发送恢复链接
                </Button>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
